import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ChangePasswordDto } from '@monorepo/shared';
import { FormInput } from '../components/ui/FormInput';
import { PrimaryButton } from '../components/ui/PrimaryButton';
import { PASSWORD_MIN_LENGTH, PASSWORD_REGEX } from '../constants/validation';
import { useChangePassword } from '../services/auth.service';

export const ChangePasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const changePasswordMutation = useChangePassword();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordDto & { confirmPassword: string }>();

  const newPassword = watch('newPassword');

  const onSubmit = async (data: ChangePasswordDto & { confirmPassword: string }) => {
    setErrorMessage('');

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      navigate('/login');
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        const errorMsg = axiosError.response?.data?.message;

        if (errorMsg === 'Current password is incorrect') {
          setErrorMessage(t('auth.change_password.error.incorrect_current_password'));
        } else if (errorMsg === 'new_password_same_as_old') {
          setErrorMessage(t('auth.change_password.error.same_as_old'));
        } else {
          setErrorMessage(t('auth.change_password.error.submit_failed'));
        }
      } else {
        setErrorMessage(t('auth.change_password.error.submit_failed'));
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-3xl font-display text-primary mb-2">
            {t('auth.change_password.title')}
          </h1>
          <p className="text-secondary">{t('auth.change_password.subtitle')}</p>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <FormInput
              label={t('auth.change_password.current_password.label')}
              type="password"
              placeholder={t('auth.change_password.current_password.placeholder')}
              error={errors.currentPassword?.message}
              required
              {...register('currentPassword', {
                required: t('auth.change_password.current_password.error.required'),
              })}
            />

            <FormInput
              label={t('auth.change_password.new_password.label')}
              type="password"
              placeholder={t('auth.change_password.new_password.placeholder')}
              error={errors.newPassword?.message}
              required
              showPasswordStrength={true}
              passwordValue={newPassword || ''}
              {...register('newPassword', {
                required: t('auth.change_password.new_password.error.required'),
                minLength: {
                  value: PASSWORD_MIN_LENGTH,
                  message: t('auth.change_password.new_password.error.min_length'),
                },
                pattern: {
                  value: PASSWORD_REGEX,
                  message: t('auth.change_password.new_password.error.too_weak'),
                },
              })}
            />

            <FormInput
              label={t('auth.change_password.confirm_password.label')}
              type="password"
              placeholder={t('auth.change_password.confirm_password.placeholder')}
              error={errors.confirmPassword?.message}
              required
              {...register('confirmPassword', {
                required: t('auth.change_password.confirm_password.error.required'),
                validate: (value) =>
                  value === watch('newPassword') ||
                  t('auth.change_password.confirm_password.error.mismatch'),
              })}
            />

            {errorMessage && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errorMessage}</p>
              </div>
            )}

            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-6 py-2 text-text hover:bg-background rounded-lg transition"
              >
                {t('common.cancel')}
              </button>
              <PrimaryButton type="submit" loading={changePasswordMutation.isPending}>
                {t('auth.change_password.cta.submit')}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
